import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { Helmet } from 'react-helmet-async';
import { quizData } from '../data';
import AdSenseAd from '../components/AdSenseAd';
import { Container, Row, Col, Card, Button, ProgressBar } from 'react-bootstrap';
import './Quiz.css';

const Quiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  const startQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
  };

  const handleAnswer = (type) => {
    const newAnswers = [...answers, type];
    setAnswers(newAnswers);

    const nextQuestion = currentQuestion + 1;
    if (nextQuestion < quizData.questions.length) {
      setCurrentQuestion(nextQuestion);
    } else {
      calculateResult(newAnswers);
      setCurrentQuestion(null);
    }
  };

  const calculateResult = (finalAnswers) => {
    const counts = finalAnswers.reduce((acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const resultType = Object.keys(counts).reduce((a, b) =>
      counts[a] > counts[b] ? a : b
    );
    setResult(quizData.results[resultType]);
  };
  
  const restartQuiz = () => {
    startQuiz();
  };

  const handleSaveAsImage = () => {
    if (resultRef.current) {
      const originalBg = resultRef.current.style.backgroundColor;
      resultRef.current.style.backgroundColor = 'white';

      html2canvas(resultRef.current).then(canvas => {
        resultRef.current.style.backgroundColor = originalBg;
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'wellness-test-result.png';
        link.click();
      });
    }
  };

  const handleShare = () => {
    const shareData = {
      title: '나의 스트레스 관리 유형은?',
      text: `저의 스트레스 관리 유형은 '${result.name}'래요! 당신의 웰니스 스타일도 궁금하다면?`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        navigator.share(shareData);
      } else {
        navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`)
          .then(() => alert('결과가 클립보드에 복사되었어요!'));
      }
    } catch (error) {
      console.error('Share failed:', error);
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`)
        .then(() => alert('공유하기가 지원되지 않아, 결과가 클립보드에 복사되었어요!'));
    }
  };

  const progress = currentQuestion !== null ? ((currentQuestion) / quizData.questions.length) * 100 : 0;

  return (
    <Container className="my-4">
      <Helmet>
        <title>나의 스트레스 관리 유형 테스트 | Wellness Lab</title>
        <meta name="description" content="간단한 질문을 통해 나의 스트레스 반응과 최적의 관리 유형을 알아보세요. 정신적 웰니스를 위한 맞춤형 조언을 제공합니다." />
        <meta property="og:title" content="나의 스트레스 관리 유형 테스트" />
        <meta property="og:description" content="당신은 어떻게 에너지를 충전하나요? 지금 바로 확인해보세요!" />
      </Helmet>
      <header className="text-center mb-5">
        <h1 className="fw-bold">나의 스트레스 관리 유형은?</h1>
        <p className="lead text-muted">내면의 평온을 찾는 첫 번째 걸음</p>
      </header>

      {/* Intro Section */}
      {currentQuestion === null && !result && (
        <Row className="justify-content-md-center">
          <Col md={10} lg={8}>
            <Card className="text-center shadow-sm intro-card">
              <Card.Body className="p-5">
                <Card.Title as="h2" className="mb-4">지친 당신을 위한 '마음 건강' 진단</Card.Title>
                <Card.Text className="mb-4">
                  숨 가쁜 일상 속에서 당신은 어떻게 스트레스를 이겨내고 있나요?
                  간단한 테스트를 통해 당신의 무의식적인 스트레스 대처 스타일을 파악하고, 에너지를 회복할 수 있는 최적의 방법을 제안해 드립니다.
                </Card.Text>
                <Button variant="primary" size="lg" onClick={startQuiz}>
                  테스트 시작하기
                </Button>
              </Card.Body>
              <Card.Footer className="text-muted py-3">
                자기 이해를 위한 심리 진단
              </Card.Footer>
            </Card>
            
            {/* SEO Text Section */}
            <div className="mt-5 p-4 bg-light rounded" style={{ textAlign: 'left' }}>
                <h2>스트레스 관리, 왜 중요할까요?</h2>
                <p>
                  현대인에게 스트레스는 피할 수 없는 동반자와 같습니다. 하지만 같은 스트레스 상황에서도 누군가는 빠르게 회복하고, 누군가는 깊은 번아웃에 빠지기도 합니다. 그 차이는 바로 자신의 '스트레스 대처 유형'을 얼마나 잘 알고 있느냐에 달려 있습니다.
                </p>
                
                <AdSenseAd slot="YOUR_QUIZ_INTRO_AD_SLOT_ID" />

                <p>
                  저희 'Wellness Lab'에서는 심리학적 관점에서 스트레스 반응을 4가지 핵심 유형으로 분류하였습니다. 이 테스트를 통해 당신이 정적인 휴식을 통해 에너지를 얻는지, 아니면 목표 달성과 성취를 통해 스트레스를 해소하는지 확인해 보세요. 자신의 유형을 아는 것만으로도 막연한 불안감을 줄이고 훨씬 효율적인 휴식을 취할 수 있습니다.
                </p>
                <h3 className="mt-4">현대인의 고질병, 번아웃 증후군 예방하기</h3>
                <p>
                  최근 많은 직장인과 학생들 사이에서 화두가 되고 있는 '번아웃 증후군'은 스트레스가 만성화되어 심신이 기진맥진해진 상태를 말합니다. 번아웃을 예방하기 위해서는 거창한 휴가보다 '매일의 작은 휴식'이 더 중요합니다. 자신의 성향에 맞지 않는 휴식법(예를 들어 정적인 휴식이 필요한 사람이 억지로 활동적인 모임에 나가는 것)은 오히려 또 다른 스트레스가 될 수 있습니다.
                </p>
                <p>
                  본 테스트는 당신에게 꼭 맞는 '휴식의 기술'을 찾는 이정표가 되어줄 것입니다. 테스트 결과와 함께 제공되는 블로그의 건강 아티클들을 통해 혈당 관리, 도파민 디톡스, 수면 위생 등 구체적인 실천 방안도 함께 확인해 보세요.
                </p>
                <h3 className="mt-4">Wellness Lab과 함께하는 마음 챙김</h3>
                <p>
                  건강한 삶은 단순히 질병이 없는 상태가 아니라, 신체적, 정신적, 사회적으로 완전히 안녕한 상태를 의미합니다. 저희 연구소는 여러분이 자신만의 속도를 찾고 일상의 균형을 회복할 수 있도록 돕습니다. 테스트 결과를 친구나 동료와 공유하며 서로의 스트레스 해소법을 이야기 나누어 보는 것도 좋은 소통의 방법이 될 것입니다.
                </p>
            </div>
          </Col>
        </Row>
      )}

      {/* Quiz Section */}
      {currentQuestion !== null && (
        <Row className="justify-content-md-center">
          <Col md={10} lg={8}>
            <Card className="shadow-sm">
              <Card.Header>
                <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
              </Card.Header>
              <Card.Body className="text-center p-5">
                <Card.Title as="h4" className="mb-5 question-text">{quizData.questions[currentQuestion].text}</Card.Title>
                <div className="d-grid gap-3">
                  {quizData.questions[currentQuestion].answers.map((answer, index) => (
                    <Button key={index} variant="outline-primary" size="lg" onClick={() => handleAnswer(answer.type)}>
                      {answer.text}
                    </Button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Result Section */}
      {result && (
        <Row className="justify-content-md-center">
          <Col md={10} lg={8}>
            <Card className="text-center shadow-sm" ref={resultRef}>
              <Card.Header as="h2" className="py-3">분석 결과</Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={5}>
                    <Card.Img variant="top" src={result.imageUrl} alt={result.name} className="result-image" />
                  </Col>
                  <Col md={7} className="text-md-start mt-4 mt-md-0">
                    <h3 className="fw-bold">{result.name}</h3>
                    <p className="result-description">
                      {result.description}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer>
                <Row>
                  <Col md={4} className="d-grid mb-2 mb-md-0"><Button variant="secondary" onClick={handleSaveAsImage}>결과 이미지 저장</Button></Col>
                  <Col md={4} className="d-grid mb-2 mb-md-0"><Button variant="info" onClick={handleShare}>결과 공유하기</Button></Col>
                  <Col md={4} className="d-grid"><Button variant="primary" onClick={restartQuiz}>다시 테스트하기</Button></Col>
                </Row>
              </Card.Footer>
            </Card>
            <AdSenseAd slot="YOUR_QUIZ_RESULT_AD_SLOT_ID" />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Quiz;
